import './DataPointCalculator.scss';
import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import classNames from 'classnames';
import hashObject from 'object-hash';
import cloneDeep from 'lodash/cloneDeep';
import {nanoid} from "nanoid";
import {useDodWizard} from "@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext";
import {shortNumber, safeStringEquals} from "@/utils";
import {estimateExtractDataPoints} from "@/api";
import {DodFilters, DodLayoutConfig, DodRunConfig} from '@/types/DodRun';
import { DATA_POINTS_LIMIT } from '@/constants/dod.constants';

const baseClassName = 'data-point-calculator';

export type DataPointCalculatorRef = {
    calculate(runConfig: DodRunConfig, sessionId: string): Promise<number | undefined>;
    value: number;
}

export type DataPointCalculatorProps = {
    byzRef: React.Ref<DataPointCalculatorRef>
};

export function DataPointCalculator({byzRef}: DataPointCalculatorProps) {

    const {runConfig, setDataPointCount, setCalculatingDataPoints, allExceptSelectionFields, sessionId} = useDodWizard();
    const [lastUsedRunConfig, setLastUsedRunConfig] = useState<DodRunConfig>()
    const [value, setValue] = useState<number>(0);
    const [busy, setBusy] = useState(false);
    const calcIdRef = useRef<string | null>(null);
    const lastConfigHash = useRef<string>('');

    const dataPointsLimitExceeded: boolean = value > DATA_POINTS_LIMIT;

    useEffect(() => {
        calculate(runConfig, sessionId);
    }, [runConfig]);

    useImperativeHandle(byzRef, () => ({
        calculate,
        value
    }), [value])

    function getFiltersWithExcludeValues(filters: DodFilters): DodFilters {
        const allExceptSelectionFieldsSet = new Set(allExceptSelectionFields);
        return {
            ...filters,
            ...Object.fromEntries(
                Object.entries(filters).map(([key, value]) => {
                    if (allExceptSelectionFieldsSet.has(key)) {
                        return [key, { ...value, isExcluded: true }];
                    }
                    if (key === 'characteristics') {
                        const characteristics = value;
                        const characteristicsWithExcludeValues = Object.fromEntries(
                            Object.entries(characteristics).map(([key, value]) => {
                                if (allExceptSelectionFieldsSet.has(key)) {
                                    return [key, { ...value, isExcluded: true }];
                                }
                                return [key, value];
                            })
                        );
                        return [key, characteristicsWithExcludeValues];
                    }
                    if (key === 'customCharacteristics') {
                        const customCharacteristics = value;
                        const customCharacteristicsWithExcludeValues = Object.fromEntries(
                            Object.entries(customCharacteristics).map(([key, value]) => {
                                // @ts-ignore
                                if (allExceptSelectionFieldsSet.has(parseInt(key)) || allExceptSelectionFieldsSet.has(key)) {
                                    return [key, { ...value, isExcluded: true }];
                                }
                                return [key, value];
                            })
                        );
                        return [key, customCharacteristicsWithExcludeValues];
                    }
                    if(key === 'ppgs') {
                        const ppgs = value;
                        const ppgsWithExcludeValues = Object.fromEntries(
                            Object.entries(ppgs).map(([key, value]) => {
                                // @ts-ignore
                                if (allExceptSelectionFieldsSet.has(parseInt(key)) || allExceptSelectionFieldsSet.has(key)) {
                                    return [key, { ...value, isExcluded: true }];
                                }
                                return [key, value];
                            })
                        );
                        return [key, ppgsWithExcludeValues];
                    }
                    return [key, value];
                })
            ),
        };
    }

    async function calculate(runConfig: DodRunConfig, sessionId: string): Promise<number | undefined> {
        // prevent unnecessary recalculations
        const {layout, ...rest} = runConfig;
        const hash = hashObject({
            rest,
            layout: shrinkLayoutConfig(layout)
        }, {
            excludeKeys(key: string) { // these selections will not trigger a recalculation of data points
                return ['quickLayoutCode', 'savedLayoutId', 'order', 'pageBy', 'sortType', 'type'].includes(key)
            }
        });

        if (safeStringEquals(lastConfigHash.current, hash)) {
            return value;
        }

        const calcId = calcIdRef.current = nanoid();
        lastConfigHash.current = hash;

        try {
            if (!runConfig?.filters.categories.values.length && !runConfig?.filters.categories.summedSelections.length) {
                setValue(0);
                setDataPointCount(0);
                return 0;
            }

            setBusy(true);
            setCalculatingDataPoints(true);

            setLastUsedRunConfig(runConfig);
            // todo: add logic to cancel previous calculate requests
            let clonedRunConfig = cloneDeep(runConfig);
            clonedRunConfig = {...clonedRunConfig, filters: getFiltersWithExcludeValues(clonedRunConfig.filters)};
            const {
                estimate: dataPoints, 
                approximate // todo: We may eventually want to use this flag to show a warning or sonething when the estimate is approximate
            } = await estimateExtractDataPoints(clonedRunConfig, sessionId);
            // this is a guard to prevent an old long-running calculation from overwriting the most recent calculation
            if (safeStringEquals(calcId, calcIdRef.current)) {
                setValue(dataPoints);
                setDataPointCount(dataPoints);
            }
            return dataPoints;
        } catch (err) {
            if (safeStringEquals(calcId, calcIdRef.current)) {
                setValue(0);
                setDataPointCount(0);
            }
            return 0;
        } finally {
            if (safeStringEquals(calcId, calcIdRef.current)) {
                setBusy(false);
                setCalculatingDataPoints(false);
            }
        }
    }

    async function handleRefreshClick(): Promise<void> {

        if (!lastUsedRunConfig || busy) return;

        await calculate(lastUsedRunConfig, sessionId);
    }

    return (
        <div
            className={classNames(baseClassName, {
                [`${baseClassName}--calculating`]: busy,
                [`${baseClassName}--over-limit`]: dataPointsLimitExceeded && !busy,
            })}
        >
            <div className={`${baseClassName}__label`}>Data Size</div>
            <div className={`${baseClassName}__value`}>
                {busy ? 'Calculating...' : `${value && Number(value) !== 0 ? shortNumber(value) : 'No'} Data Points`}
            </div>
            <div className={`${baseClassName}__refresh-trigger`} onClick={handleRefreshClick} />
        </div>
    );
}

/**
 * Strips unimportant values from the layout and then normalizes the struture
 * data-point should recalculate when hides/shows a product field, adds/removes a condition,
 * adds/removes Category Totals and stacks by a product field
 */
function shrinkLayoutConfig({rows, columns, includeCategoryTotals, includeSubTotals}: DodLayoutConfig): object {

    // shrink layout object to compare with previous row cols and prevent unnecessary data recalculations
    return {
        includeCategoryTotals,
        includeSubTotals,
        products: [...rows, ...columns].filter((item) => item.type === 'products' && item.dim)
            .sort((a, b) => a.dim.localeCompare(b.dim))
    }
}

export default DataPointCalculator;
